import { useState, useEffect, useRef } from 'react';
import { resolveB2Url, refreshB2Url } from '../api/storageResolver';
import MediaCover from './MediaCover';

const SPEEDS = [1, 1.25, 1.5, 0.75];

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function Player({ item, initialPosition = 0, seekNonce = 0, onPositionChange = () => {}, onBookmark = () => {}, onClose }) {
  const mediaRef        = useRef(null);
  const containerRef    = useRef(null);
  const idleTimerRef    = useRef(null);
  const posTimerRef     = useRef(null);
  const refreshTimerRef = useRef(null);
  const wakeLockRef     = useRef(null);
  const onPositionChangeRef = useRef(onPositionChange);
  useEffect(() => { onPositionChangeRef.current = onPositionChange; });

  const [srcUrl, setSrcUrl]             = useState(null);
  const [playing, setPlaying]           = useState(false);
  const [currentTime, setCurrentTime]   = useState(initialPosition);
  const [duration, setDuration]         = useState(0);
  const [speedIdx, setSpeedIdx]         = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [bookmarkSaved, setBookmarkSaved] = useState(false);
  const seekNonceRef = useRef(seekNonce);

  const isVideo    = item.MediaType === 'Movie' || item.MediaType === 'Video';
  const skipAmount = item.MediaType === 'Audiobook' ? 30 : 10;

  useEffect(() => {
    let cancelled = false;
    resolveB2Url(item.StorageLocation).then(url => {
      if (!cancelled) setSrcUrl(url);
    });
    return () => { cancelled = true; };
  }, [item.StorageLocation]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !srcUrl) return;

    function onLoadedMetadata() {
      setDuration(media.duration);
      if (initialPosition > 0) media.currentTime = initialPosition;
    }
    function onTimeUpdate() { setCurrentTime(media.currentTime); }
    function onPlay() {
      setPlaying(true);
      if (navigator.wakeLock) {
        navigator.wakeLock.request('screen')
          .then(s => { wakeLockRef.current = s; })
          .catch(() => {});
      }
    }
    function onPause() {
      setPlaying(false);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      if (!media.ended && media.currentTime > 0) onPositionChangeRef.current(Math.floor(media.currentTime));
    }
    function onEnded() {
      setPlaying(false);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      onPositionChangeRef.current(0);
    }

    media.addEventListener('loadedmetadata', onLoadedMetadata);
    media.addEventListener('timeupdate',     onTimeUpdate);
    media.addEventListener('play',           onPlay);
    media.addEventListener('pause',          onPause);
    media.addEventListener('ended',          onEnded);

    posTimerRef.current = setInterval(() => {
      if (media && !media.paused && !media.ended) onPositionChangeRef.current(Math.floor(media.currentTime));
    }, 30_000);

    refreshTimerRef.current = setInterval(async () => {
      const newUrl = await refreshB2Url(item.StorageLocation);
      if (!newUrl || !mediaRef.current) return;
      const m = mediaRef.current;
      const wasPlaying = !m.paused;
      const pos = m.currentTime;
      m.src = newUrl;
      m.load();
      m.currentTime = pos;
      if (wasPlaying) m.play().catch(() => {});
    }, 45 * 60 * 1000);

    return () => {
      media.removeEventListener('loadedmetadata', onLoadedMetadata);
      media.removeEventListener('timeupdate',     onTimeUpdate);
      media.removeEventListener('play',           onPlay);
      media.removeEventListener('pause',          onPause);
      media.removeEventListener('ended',          onEnded);
      clearInterval(posTimerRef.current);
      clearInterval(refreshTimerRef.current);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [srcUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isVideo || !showControls) return;
    idleTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    return () => clearTimeout(idleTimerRef.current);
  }, [isVideo, showControls]);

  // Save on page hide (tab close/reload) and on unmount (close player / in-app nav away).
  useEffect(() => {
    function saveNow() {
      const m = mediaRef.current;
      if (m && !m.ended && m.currentTime > 0) onPositionChangeRef.current(Math.floor(m.currentTime));
    }
    window.addEventListener('pagehide', saveNow);
    window.addEventListener('beforeunload', saveNow);
    return () => {
      window.removeEventListener('pagehide', saveNow);
      window.removeEventListener('beforeunload', saveNow);
      saveNow();   // unmount = close player / route change while page stays alive → async save completes
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Imperative re-seek for a bookmark jump into the ALREADY-open chapter.
  // Fresh loads (new chapter) still seek via loadedmetadata + initialPosition; this only
  // covers the same-src case where loadedmetadata won't fire again. The ref-guard skips the
  // mount run, and the duration guard means a not-yet-loaded media is left to loadedmetadata.
  useEffect(() => {
    if (seekNonce === seekNonceRef.current) return; // mount / no change
    seekNonceRef.current = seekNonce;
    const media = mediaRef.current;
    if (media && Number.isFinite(media.duration) && media.duration > 0) {
      media.currentTime = initialPosition;
    }
  }, [seekNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  if (item.MediaType === 'Photo') return null;

  function togglePlay() {
    const media = mediaRef.current;
    if (!media) return;
    if (media.paused) media.play().catch(() => {});
    else media.pause();
  }

  function skip(seconds) {
    const media = mediaRef.current;
    if (!media) return;
    media.currentTime = Math.max(0, Math.min(media.duration || 0, media.currentTime + seconds));
  }

  function handleSeek(e) {
    const media = mediaRef.current;
    if (!media) return;
    const t = Number(e.target.value);
    media.currentTime = t;
    setCurrentTime(t);
  }

  function cycleSpeed() {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (mediaRef.current) mediaRef.current.playbackRate = SPEEDS[next];
  }

  function handleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  }

  function revealControls() { setShowControls(true); }

  function handleBookmark() {
    const media = mediaRef.current;
    if (!media) return;
    const position = media.currentTime;                 // EXACT time at tap — not the 30s poll
    onBookmark({ position, label: `Bookmark ${formatTime(position)}` });
    setBookmarkSaved(true);
    setTimeout(() => setBookmarkSaved(false), 1200);    // brief in-button confirmation, no prompt
  }

  const progress = duration > 0 ? currentTime : 0;
  const timeLabel = `${formatTime(currentTime)} / ${formatTime(duration)}`;

  if (isVideo) {
    return (
      <div
        ref={containerRef}
        className="fv-player-video"
        onMouseMove={revealControls}
        onTouchStart={revealControls}
        onClick={revealControls}
      >
        <video
          ref={mediaRef}
          className="fv-player-video-el"
          src={srcUrl || undefined}
          playsInline
          x-webkit-airplay="allow"
          preload="metadata"
        />
        <button className="fv-player-close" onClick={e => { e.stopPropagation(); onClose(); }} aria-label="Close">
          ✕
        </button>
        <div className={`fv-player-video-controls${showControls ? '' : ' fv-player-video-controls--hidden'}`}>
          <p className="fv-player-video-title">{item.Title}</p>
          <input
            type="range"
            className="fv-player-seek"
            min={0} max={duration || 0} step={1} value={progress}
            onChange={handleSeek}
            onClick={e => e.stopPropagation()}
          />
          <div className="fv-player-controls-row">
            <button className="fv-player-btn" onClick={e => { e.stopPropagation(); skip(-skipAmount); }} aria-label={`Back ${skipAmount}s`}>
              −{skipAmount}s
            </button>
            <button className="fv-player-btn fv-player-btn--play" onClick={e => { e.stopPropagation(); togglePlay(); }} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? '⏸' : '▶'}
            </button>
            <button className="fv-player-btn" onClick={e => { e.stopPropagation(); skip(skipAmount); }} aria-label={`Forward ${skipAmount}s`}>
              +{skipAmount}s
            </button>
            <span className="fv-player-controls-spacer" />
            <button className="fv-player-btn" onClick={e => { e.stopPropagation(); cycleSpeed(); }} aria-label="Speed">
              {SPEEDS[speedIdx]}×
            </button>
            <button className="fv-player-btn" onClick={e => { e.stopPropagation(); handleFullscreen(); }} aria-label="Fullscreen">
              ⛶
            </button>
            <span className="fv-player-time">{timeLabel}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-player-audio">
      <audio
        ref={mediaRef}
        src={srcUrl || undefined}
        preload="metadata"
      />
      <button
        className="fv-player-close fv-player-close--audio"
        onClick={onClose}
        aria-label="Close"
      >
        ✕
      </button>
      <div className="fv-player-audio-inner">
        <MediaCover item={item} aspectRatio="1:1" size="sm" />
        <div className="fv-player-audio-info">
          <p className="fv-player-audio-title">{item.Title}</p>
          <input
            type="range"
            className="fv-player-seek"
            min={0} max={duration || 0} step={1} value={progress}
            onChange={handleSeek}
          />
          <div className="fv-player-controls-row">
            <button className="fv-player-btn" onClick={() => skip(-skipAmount)} aria-label={`Back ${skipAmount}s`}>
              −{skipAmount}s
            </button>
            <button className="fv-player-btn fv-player-btn--play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? '⏸' : '▶'}
            </button>
            <button className="fv-player-btn" onClick={() => skip(skipAmount)} aria-label={`Forward ${skipAmount}s`}>
              +{skipAmount}s
            </button>
            <span className="fv-player-controls-spacer" />
            {item.MediaType === 'Audiobook' && (
              <button className="fv-player-btn" onClick={handleBookmark} aria-label="Bookmark this moment">
                {bookmarkSaved ? '✓' : '🔖'}
              </button>
            )}
            <button className="fv-player-btn" onClick={cycleSpeed} aria-label="Speed">
              {SPEEDS[speedIdx]}×
            </button>
            <span className="fv-player-time">{timeLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
